import {
	createLocalDataSource,
	useFilterHandler,
	useSortHandler
} from "client/Hooks";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	ConfirmationDialog,
	Icon,
	IconButton,
	Pagination,
	PersonAvatar,
	TableView,
	TableViewNoResultConfig,
	Tag
} from "client/Components";
import { contains } from "client/Data/searching";
import { localize } from "client_legacy/ndaw/culture";
import { DataSourceSortableState, DataSourceSortDirection } from "client/Data/sorting";
import { UserCollectionCell as Cell } from "./UserCollectionCell";
import { UserAndUserGroupKind } from "./UserAndUserGroupCollection";
import { UserGridItem } from "client/Api/UserManagement/userManagement";
import {
	generateTrackingId,
	isNil
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	UserDisplayItem,
	convertToDisplayItem
} from "../misc";
import { UserLicenseFilterForm } from "../UserLicenseFilterForm";
import { UserLicense } from "client/Api/FeaturePackages/featurePackageManagement";
import { FirstnameCellContent } from "../FirstnameCellContent";

const {
	Row,
} = TableView;

export type UserCollectionProps = {
	allData: readonly UserGridItem[];
	value: readonly number[];
	searchTerm: string;
	mode: UserCollectionMode;
	emptyMessages?: EmptyMessages;
	height?: "fill" | "stretch";
	requiredUserLicense?: UserLicense | null;
};

export const UserCollection = (props: UserCollectionProps): ReactElement => {
	const {
		allData,
		value,
		mode,
		searchTerm,
		emptyMessages = {},
		height,
		requiredUserLicense
	} = props;

	const [filterState, setFilterState] = useState<FilterState>({ licenses: [] });
	const [sortingState, setSortingState] = useState<DataSourceSortableState<UserDisplayItem>>({
		propertyName: "firstName",
		direction: DataSourceSortDirection.Ascending
	});
	const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
	const [isConfirmationOpen, setIsConfirmationOpen] = useState<boolean>(false);

	const tableRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const { sortingFor } = useSortHandler<UserDisplayItem>(sortingState, setSortingState);
	const { filteringFor } = useFilterHandler<FilterState>(filterState, setFilterState);

	const getCanBeDeleted = useCallback(
		(userId: number): boolean => {
			if (mode.kind === UserAndUserGroupKind.Read) {
				return false;
			}

			return isNil(mode.getCanBeDeleted)
				? true
				: mode.getCanBeDeleted(userId);
		},
		[mode]
	);

	const noResultConfig = useMemo(
		(): TableViewNoResultConfig => {
			const {
				noSearchResult = {
					icon: "magnifying-glass",
					title: localize("_NoSearchResults"),
					message: localize("_PleaseTrySearchingWithAnotherTerm")
				},
				empty = {
					icon: "user",
					title: localize("_NoUsersYet"),
					message: localize("_NoUsersHaveBeenAddedYet")
				}
			} = emptyMessages;

			return searchTerm
				? noSearchResult
				: empty;
		},
		[emptyMessages, searchTerm]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(searchTerm);
			setCurrentPageNumber(0);
		},
		[searchTerm]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(
				currentPageNumber,
				sortingState,
				searchTerm
			);

			tableRef.current
				?.scrollTo(0, 0);
		},
		[
			currentPageNumber,
			sortingState,
			searchTerm
		]
	);

	const displayItems: readonly UserDisplayItem[] = useMemo(
		(): readonly UserDisplayItem[] => {
			return allData.filter(item => value.includes(item.id))
				.map((x) => convertToDisplayItem(x, requiredUserLicense));
		},
		[
			allData,
			requiredUserLicense,
			value
		]
	);

	const { currentItems, pagination } = useUserCollectionData(
		displayItems,
		{
			searchTerm,
			sorting: sortingState,
			pagination: {
				amountPerPage: 100,
				currentPageNumber
			},
			filtering: filterState
		}
	);

	const deleteHandler = useCallback(
		(id: number): void => {
			if (mode.kind === UserAndUserGroupKind.Read) {
				throw new Error("Delete is not supported in read mode");
			}

			mode.onChange(value.filter(item => item !== id));
		},
		[
			mode,
			value
		]
	);

	const deleteAllConfirmedHandler = useCallback(
		(): void => {
			if (mode.kind === UserAndUserGroupKind.Read) {
				throw new Error("Delete all is not supported in read mode");
			}

			const newValue: readonly number[] = value.filter(x => !getCanBeDeleted(x));

			mode.onChange(newValue);

			setIsConfirmationOpen(false);
		},
		[
			mode,
			getCanBeDeleted,
			value
		]
	);

	const hasDeletableItems: boolean = useMemo(
		(): boolean => {
			return value.some(x => getCanBeDeleted(x));
		},
		[
			value,
			getCanBeDeleted
		]
	);

	return (
		<>
			<TableView
				trackingId="user-collection"
				columns={[
					mode.kind === UserAndUserGroupKind.Edit && {
						title: currentItems.length && hasDeletableItems
							? (
								<IconButton
									kind="transparent"
									icon="trash-can"
									onClick={() => setIsConfirmationOpen(true)}
									trackingId="delete-button"
								/>
							)
							: "",
						width: 48,
						minWidth: 48,
						trackingId: "delete"
					},
					{
						title: "",
						width: 40,
						minWidth: 40,
						trackingId: "avatar"
					},
					{
						title: localize("_FirstName"),
						sortable: sortingFor("firstName"),
						trackingId: "first-name"
					},
					{
						title: localize("_LastName"),
						sortable: sortingFor("lastName"),
						trackingId: "last-name"
					},
					{
						title: localize("_Email"),
						sortable: sortingFor("primaryEmail"),
						trackingId: "email"
					},
					{
						title: localize("_Username"),
						sortable: sortingFor("userPrincipalName"),
						trackingId: "userName"
					},
					{
						title: localize("_Status"),
						sortable: sortingFor("isActive"),
						trackingId: "is-active"
					},
					{
						title: localize("_Package"),
						sortable: sortingFor("userLicenseTag"),
						trackingId: "package",
						filterable: {
							columnFilterableFormFactory: (onClose) => (
								<UserLicenseFilterForm
									filterable={filteringFor("licenses")}
									onClose={onClose}
								/>
							),
							isActive: filterState.licenses.length > 0
						}
					},
					{
						title: localize("_GuestUser"),
						sortable: sortingFor("isGuest"),
						trackingId: "is-guest"
					},
					{
						title: localize("_Administrator"),
						sortable: sortingFor("isAdmin"),
						trackingId: "is-admin"
					}
				]}
				noResultConfig={noResultConfig}
				ref={tableRef}
				items={currentItems}
				itemKey="id"
				height={height}
				shouldEvaluateBackgroundColor
			>
				{
					item => (
						<Row trackingId={item.id.toString()}>
							{mode.kind === UserAndUserGroupKind.Edit && (
								<Cell>
									{getCanBeDeleted(item.id) && (
										<IconButton
											kind="transparent"
											icon="trash-can"
											onClick={() => deleteHandler(item.id)}
											trackingId="delete-button"
										/>
									)}
								</Cell>
							)}
							<Cell>
								<PersonAvatar
									imageSrc={item.avatarUrl}
									initials={item.initials}
									size="x-small"
								/>
							</Cell>
							<Cell
								isDeleted={item.deletionDate}
								tooltip={item.firstName}
							>
								<FirstnameCellContent item={item} />
							</Cell>
							<Cell
								isDeleted={item.deletionDate}
								tooltip={item.lastName}
							>
								{item.lastName}
							</Cell>
							<Cell
								isDeleted={item.deletionDate}
								tooltip={item.primaryEmail ?? undefined}
							>
								{item.primaryEmail}
							</Cell>
							<Cell
								isDeleted={item.deletionDate}
								tooltip={item.userPrincipalName}
							>
								{item.userPrincipalName}
							</Cell>
							<Cell isDeleted={item.deletionDate}>
								<Tag
									text={localize(item.isActive ? "_Active" : "_Inactive")}
									color={item.isActive ? "success" : "danger"}
								/>
							</Cell>
							<Cell isDeleted={item.deletionDate}>
								<Tag
									{...item.userLicenseTag}
									trackingId={generateTrackingId("package-tag", `|${item.id}`)}
								/>
							</Cell>
							<Cell isDeleted={item.deletionDate}>
								{item.isGuest && <Icon value="check" size="large" />}
							</Cell>
							<Cell isDeleted={item.deletionDate}>{item.isAdmin && <Icon value="check" size="large" />}</Cell>
						</Row>
					)
				}
			</TableView>
			<Pagination {...pagination} onPageNumberChanged={setCurrentPageNumber} />
			{ (isConfirmationOpen && mode.kind === UserAndUserGroupKind.Edit) && (
				<ConfirmationDialog
					trackingId="remove-all-users-confirmation-dialog"
					title={localize("_AreYouSureYouWantToRemoveAllUsers")}
					confirmText={localize("_Remove")}
					cancelText={localize("_Cancel")}
					onCancellation={() => setIsConfirmationOpen(false)}
					onConfirmation={deleteAllConfirmedHandler}
				/>
			)}
		</>
	);
};

UserCollection.displayName = "UserCollection";

type FilterState = {
	licenses: readonly UserLicense[];
};

function applyFilter(
	items: readonly UserDisplayItem[],
	filter: FilterState
): readonly UserDisplayItem[] {
	if (filter.licenses.length === 0) {
		return items;
	}

	return items.filter(
		item => {
			const userLicense: UserLicense | null = item.userLicenseTag.value;

			if (isNil(userLicense)) {
				return false;
			}

			return filter.licenses
				.includes(userLicense);
		}
	);
}

const useUserCollectionData = createLocalDataSource<UserDisplayItem, FilterState>()
	.setConfiguration({
		search: {
			doesMatchItem: (item, searchTerm) =>
				contains(item.firstName, searchTerm)
				|| contains(item.lastName, searchTerm)
				|| contains(item.userPrincipalName, searchTerm)
				|| contains(item.primaryEmail ?? "", searchTerm)
		},
		hasPagination: true,
		filtering: {
			applyFilter
		},
	})
	.getHook();

export type UserCollectionMode = {
	kind: UserAndUserGroupKind.Read;
} | {
	kind: UserAndUserGroupKind.Edit;
	onChange(value: readonly number[]): void;
	getCanBeDeleted?: (userId: number) => boolean;
};

export type EmptyMessages = {
	noSearchResult?: TableViewNoResultConfig;
	empty?: TableViewNoResultConfig;
};

export const userCollectionSearchPropertyKeys: readonly [LocalizationKey, ...LocalizationKey[]] = [
	"_Name",
	"_Email",
	"_Username"
];
